import React, { CSSProperties } from 'react';
import { Answer } from '../content';
import { AFTER_FADE, stageClass, usePlayOnce } from '../motion';
import { Rail, shareLabel } from './Rail';

// An answer as an abacus: one rail per outcome, its pushed beads the share. A score adds its
// expected level on a short scale one row beneath. On reveal the beads slide across, rail by
// rail. The drawing ends just under its last line, so the text below sits close.

const W = 280;
const RAIL0 = 72;
const RAIL1 = 232;
const ROW = 26;
const TOP = 14;

const COLOUR = { binary: 'hsl(var(--brand-pink))', single: 'hsl(var(--brand-mint))', ordinal: 'hsl(var(--brand-amber))' };

export function Abacus({ answer }: { answer: Answer }) {
  const [ref, stage] = usePlayOnce<SVGSVGElement>();
  const rows = answer.outcomes.length;
  const scaleY = TOP + rows * ROW;
  const height = answer.expected === undefined ? TOP + (rows - 1) * ROW + 10 : scaleY + 20;
  const levels = rows - 1;
  const expectedX = answer.expected === undefined ? 0 : RAIL0 + (answer.expected / levels) * (RAIL1 - RAIL0);

  return (
    <svg
      ref={ref}
      className={`oh-fig oh-abacus ${stageClass(stage)}`}
      viewBox={`0 0 ${W} ${height}`}
      style={{ maxWidth: W, '--c': COLOUR[answer.kind] } as CSSProperties}
      role="img"
      aria-label={answer.outcomes.map(([name, p]) => `${name} ${shareLabel(p)}`).join(', ')}
    >
      {answer.outcomes.map(([name, p], i) => {
        const y = TOP + i * ROW;
        const top = i === 0;
        return (
          <g key={name} className={top ? 'is-top' : 'is-other'}>
            <text className={`oh-name ${top ? 'oh-strong' : ''}`} x={0} y={y + 4.5}>
              {answer.expected !== undefined && <tspan className="oh-level">{levels - i} </tspan>}
              {name}
            </text>
            <Rail x0={RAIL0} x1={RAIL1} y={y} p={p} delay={AFTER_FADE + i * 0.14} />
            <text className={`oh-pct ${top ? 'oh-strong' : ''}`} x={W} y={y + 4} textAnchor="end" style={{ '--d': `${(AFTER_FADE + 0.55 + i * 0.14).toFixed(2)}s` } as CSSProperties}>
              {shareLabel(p)}
            </text>
          </g>
        );
      })}
      {answer.expected !== undefined && (
        <g className="oh-expected" style={{ '--d': `${(AFTER_FADE + 0.6 + rows * 0.14).toFixed(2)}s` } as CSSProperties}>
          <line className="oh-hair" x1={RAIL0} y1={scaleY} x2={RAIL1} y2={scaleY} />
          {Array.from({ length: levels + 1 }, (_, level) => {
            const x = RAIL0 + (level / levels) * (RAIL1 - RAIL0);
            return (
              <g key={level}>
                <line className="oh-hair" x1={x} y1={scaleY - 3} x2={x} y2={scaleY + 3} />
                <text className="oh-tick-label" x={x} y={scaleY + 16} textAnchor="middle">{level}</text>
              </g>
            );
          })}
          <line className="oh-mark" x1={expectedX} y1={scaleY - 7} x2={expectedX} y2={scaleY + 7} />
          <text className="oh-name" x={0} y={scaleY + 4.5}>expected</text>
          <text className="oh-pct oh-strong" x={W} y={scaleY + 4} textAnchor="end">{answer.expected.toFixed(2)}</text>
        </g>
      )}
    </svg>
  );
}

export default Abacus;
